import "./StationTransfer.scss";
import {useEffect, useMemo, useState} from "react";
import {Loader} from "rsuite";
import {
  Common_ControlBlockDto,
  StationTransferItemDto,
  StationTransferPlanDto,
  StationTransferResultDto,
} from "../../../api";
import JournalApi from "../../../services/Journal.api";
import {SharedApi} from "../../../services/Shared.api";
import {getApiErrorMessage} from "../../../services/ApiErrorMessage";
import {formatDateTime} from "./journal.utils";

type StationTransferProps = {
  // Станция, на странице которой открыт перенос
  stationId: string;
  // Она сейчас в сети (null — неизвестно)
  online: boolean | null;
  onClose: () => void;
  // После переноса — перечитать снимок
  onDone: () => void;
};

// «to» — с другой станции на эту (эта новая), «from» — с этой на другую (эта вышла из строя)
type Direction = "to" | "from";

const NETWORK_GROUP = "Связь";

const stationTitle = (block?: Common_ControlBlockDto) => {
  if (!block) return "Станция";
  const name = block.description || block.clockStation?.description || "Без названия";
  const serial = block.clockStation?.serialNumber ? `ЧС ${block.clockStation.serialNumber}` : block.serialNumber;
  return serial ? `${name} · ${serial}` : name;
};

const groupItems = (items: StationTransferItemDto[]) => {
  const result: { group: string; items: StationTransferItemDto[] }[] = [];
  for (const item of items) {
    const name = item.group || "Прочее";
    let group = result.find((x) => x.group === name);
    if (!group) {
      group = {group: name, items: []};
      result.push(group);
    }
    group.items.push(item);
  }
  return result;
};

// Значение без повтора названия настройки: «Часовой пояс: UTC+3» → «UTC+3»
const valueText = (item: StationTransferItemDto, value?: string | null) => {
  if (!value) return "—";
  const prefix = `${item.title}: `;
  return value.toLowerCase().startsWith(prefix.toLowerCase()) ? value.slice(prefix.length) : value;
};

const StationTransfer = ({stationId, online, onClose, onDone}: StationTransferProps) => {
  const [direction, setDirection] = useState<Direction>(online === false ? "from" : "to");
  const [blocks, setBlocks] = useState<Common_ControlBlockDto[]>([]);
  const [otherId, setOtherId] = useState("");
  const [plan, setPlan] = useState<StationTransferPlanDto | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [targetOnline, setTargetOnline] = useState<boolean | null>(null);
  const [confirming, setConfirming] = useState(false);
  const [applying, setApplying] = useState(false);
  const [result, setResult] = useState<StationTransferResultDto | null>(null);

  useEffect(() => {
    let cancelled = false;
    SharedApi.getControlBlocks()
      .then((list) => {
        if (!cancelled) setBlocks((list ?? []).filter((x) => x.id && x.id !== stationId));
      })
      .catch((err) => {
        if (!cancelled) setError(getApiErrorMessage(err, "Не удалось загрузить список станций"));
      });
    return () => {
      cancelled = true;
    };
  }, [stationId]);

  const sortedBlocks = useMemo(
    () => [...blocks].sort((a, b) => stationTitle(a).localeCompare(stationTitle(b), "ru")),
    [blocks],
  );

  const sourceId = direction === "to" ? otherId : stationId;
  const targetId = direction === "to" ? stationId : otherId;
  const other = blocks.find((x) => x.id === otherId);
  const sourceTitle = direction === "to" ? stationTitle(other) : "эта станция";
  const targetTitle = direction === "to" ? "эта станция" : stationTitle(other);

  // План: что будет записано, что уже совпадает, что перенести нельзя
  useEffect(() => {
    setPlan(null);
    setResult(null);
    setConfirming(false);
    if (!otherId) return undefined;
    let cancelled = false;
    setLoading(true);
    setError(null);
    JournalApi.getStationTransferPlan(targetId, sourceId)
      .then((value) => {
        if (cancelled) return;
        setPlan(value);
        setSelected(new Set((value.items ?? []).filter((x) => x.selected).map((x) => x.id ?? "")));
      })
      .catch((err) => {
        if (!cancelled) setError(getApiErrorMessage(err, "Не удалось подготовить перенос"));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [otherId, sourceId, targetId]);

  // Записывать можно только на станцию, которая сейчас в сети
  useEffect(() => {
    setTargetOnline(null);
    if (!targetId) return undefined;
    let cancelled = false;
    SharedApi.getControlBlocksStatus(targetId)
      .then((status) => {
        if (!cancelled) setTargetOnline(Boolean(status?.isConnected));
      })
      .catch(() => {
        if (!cancelled) setTargetOnline(null);
      });
    return () => {
      cancelled = true;
    };
  }, [targetId]);

  const groups = useMemo(() => groupItems(plan?.items ?? []), [plan]);
  const devices = plan?.devices ?? [];
  const missingDevices = devices.filter((x) => !x.found);
  const selectedCount = (plan?.items ?? []).filter((x) => x.available && selected.has(x.id ?? "")).length;

  const toggle = (id: string, on: boolean) => {
    setSelected((current) => {
      const next = new Set(current);
      if (on) next.add(id);
      else next.delete(id);
      return next;
    });
  };

  const toggleGroup = (items: StationTransferItemDto[], on: boolean) => {
    setSelected((current) => {
      const next = new Set(current);
      for (const item of items) {
        if (!item.available || !item.id) continue;
        if (on) next.add(item.id);
        else next.delete(item.id);
      }
      return next;
    });
  };

  const apply = () => {
    if (!plan) return;
    setApplying(true);
    setError(null);
    const itemIds = (plan.items ?? []).filter((x) => x.available && selected.has(x.id ?? "")).map((x) => x.id ?? "");
    JournalApi.transferStationConfig(targetId, {sourceId, itemIds})
      .then((value) => {
        setResult(value);
        setConfirming(false);
        if (direction === "to") onDone();
      })
      .catch((err) => setError(getApiErrorMessage(err, "Не удалось перенести настройки")))
      .finally(() => setApplying(false));
  };

  return (
    <section className="station-transfer">
      <div className="station-transfer__head">
        <h3 className="station-transfer__title">Перенос настроек</h3>
        <button type="button" className="station-transfer__close" onClick={onClose} aria-label="Закрыть">×</button>
      </div>

      <div className="station-transfer__direction">
        <label className={`station-transfer__option${direction === "to" ? " station-transfer__option--active" : ""}`}>
          <input type="radio" name="transfer-direction" checked={direction === "to"} onChange={() => setDirection("to")}/>
          С другой станции на эту
        </label>
        <label className={`station-transfer__option${direction === "from" ? " station-transfer__option--active" : ""}`}>
          <input type="radio" name="transfer-direction" checked={direction === "from"} onChange={() => setDirection("from")}/>
          С этой станции на другую
        </label>
      </div>

      <label className="station-transfer__pick">
        {direction === "to" ? "Откуда" : "Куда"}
        <select value={otherId} onChange={(e) => setOtherId(e.target.value)}>
          <option value="">Выберите станцию…</option>
          {sortedBlocks.map((block) => (
            <option key={block.id} value={block.id}>{stationTitle(block)}</option>
          ))}
        </select>
      </label>

      {loading && <div className="station-transfer__state"><Loader size="xs" content="Готовлю список настроек…"/></div>}
      {error && <div className="station-transfer__state station-transfer__state--error">{error}</div>}

      {plan && !loading && (
        <>
          <div className="station-transfer__summary">
            <div><span>Откуда:</span> <b>{sourceTitle}</b>
              {plan.sourceUpdatedAt && <> · снимок от {formatDateTime(plan.sourceUpdatedAt)}</>}</div>
            <div><span>Куда:</span> <b>{targetTitle}</b>
              {targetOnline === true && <> · в сети</>}
              {targetOnline === false && <> · <em className="station-transfer__bad">не в сети</em></>}</div>
            {devices.length > 0 && (
              <div><span>Устройства BIGLine:</span> найдено на новой станции {devices.length - missingDevices.length} из {devices.length}</div>
            )}
          </div>

          {targetOnline === false && (
            <div className="station-transfer__warn">
              Станция «{targetTitle}» не в сети — записать на неё настройки сейчас не получится.
            </div>
          )}
          {!plan.targetHasClockStation && groups.some((g) => g.items.some((x) => !x.available)) && (
            <div className="station-transfer__warn">
              У станции «{targetTitle}» нет часовой станции — первичные и вторичные часы, реле и музыку перенести нельзя.
            </div>
          )}
          {missingDevices.length > 0 && (
            <div className="station-transfer__warn">
              Не найдены на новой станции (по типу и адресу): {missingDevices.map((x) => x.label).join(", ")}.
              Их настройки будут пропущены — подключите устройства с теми же адресами и откройте перенос заново.
            </div>
          )}

          {groups.length === 0 && (
            <div className="station-transfer__state">У станции «{sourceTitle}» нет сохранённых настроек</div>
          )}

          {groups.map((group) => {
            const available = group.items.filter((x) => x.available);
            const checked = available.filter((x) => selected.has(x.id ?? "")).length;
            return (
              <div key={group.group} className="station-transfer__group">
                <label className="station-transfer__group-title">
                  <input type="checkbox" disabled={available.length === 0}
                         checked={available.length > 0 && checked === available.length}
                         onChange={(e) => toggleGroup(group.items, e.target.checked)}/>
                  <span>{group.group}</span>
                  <small>отмечено {checked} из {group.items.length}</small>
                </label>
                {group.group === NETWORK_GROUP && (
                  <div className="station-transfer__note">
                    По умолчанию не отмечено: после смены сети станция может пропасть со связи.
                  </div>
                )}
                {group.items.map((item) => (
                  <label key={item.id}
                         className={`station-transfer__item${item.available ? "" : " station-transfer__item--off"}`}>
                    <input type="checkbox" disabled={!item.available} checked={item.available && selected.has(item.id ?? "")}
                           onChange={(e) => toggle(item.id ?? "", e.target.checked)}/>
                    <span className="station-transfer__item-body">
                      <span className="station-transfer__item-title">
                        {item.deviceLabel && <span className="station-transfer__device">{item.deviceLabel}
                          {item.targetDeviceLabel && item.targetDeviceLabel !== item.deviceLabel && <> → {item.targetDeviceLabel}</>}: </span>}
                        {item.title}
                      </span>
                      <span className="station-transfer__values">
                        {item.same ? (
                          <><span>{valueText(item, item.value)}</span> <em className="station-transfer__same">уже совпадает</em></>
                        ) : (
                          <><span className="station-transfer__was">{valueText(item, item.targetValue)}</span>
                            {" → "}<b>{valueText(item, item.value)}</b></>
                        )}
                      </span>
                      {item.reason && <span className="station-transfer__reason">{item.reason}</span>}
                    </span>
                  </label>
                ))}
              </div>
            );
          })}

          {groups.length > 0 && !result && (
            <div className="station-transfer__footer">
              {!confirming ? (
                <button type="button" className="station-transfer__apply" disabled={selectedCount === 0 || targetOnline === false}
                        onClick={() => setConfirming(true)}>
                  Перенести отмеченное ({selectedCount})
                </button>
              ) : (
                <div className="station-transfer__confirm">
                  <span>Записать {selectedCount} настроек на станцию «{targetTitle}»? Каждое изменение попадёт в журнал.</span>
                  <div className="station-transfer__confirm-actions">
                    <button type="button" className="station-transfer__apply" onClick={apply} disabled={applying}>
                      {applying ? "Переношу…" : "Да, перенести"}
                    </button>
                    <button type="button" className="station-transfer__cancel" onClick={() => setConfirming(false)}
                            disabled={applying}>Отмена</button>
                  </div>
                  {applying && <Loader size="xs" content="Записываю на станцию по одной настройке…"/>}
                </div>
              )}
            </div>
          )}

          {result && (
            <div className={`station-transfer__result${(result.failed ?? 0) > 0 ? " station-transfer__result--warn" : ""}`}>
              <b>Перенесено: {result.applied ?? 0}{(result.failed ?? 0) > 0 && <>, не получилось: {result.failed}</>}.</b>
              {(result.items ?? []).filter((x) => !x.success).map((x) => (
                <div key={x.id} className="station-transfer__result-row">
                  {x.deviceLabel ? `${x.deviceLabel}: ` : ""}{x.title} — {x.error ?? "ошибка"}
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </section>
  );
};

export default StationTransfer;
